import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Layers, PlusCircle, FilterIcon, ArrowLeft, Building2 } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import DynamicFormModal from '@/components/DynamicFormModal';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { garmentFamiliesColumns, columnsExcel } from './utils/garmentFamiliesColumns';
import { createGarmentFamily, getGarmentFamilies, updateGarmentFamily, deleteGarmentFamily, exportGarmentFamilies } from './Services/garmentFamilies.services';

const GarmentFamiliesPage = () => {
  const [searchParams] = useSearchParams();
  const idLavanderia = searchParams.get('lavanderia');
  const [laundryName, setLaundryName] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(
    idLavanderia ? { query: { 'lavanderia.id': idLavanderia }, page: 1, limit: 10 } : { query: {}, page: 1, limit: 10 }
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { toast } = useToast();

  const buildBackendQuery = (rawQuery) => {
    const query = { ...rawQuery };
    if (query.nombre && typeof query.nombre === 'string') {
      const value = query.nombre.trim();
      if (value) {
        query.nombre = { $regex: value, $options: 'i' };
      } else {
        delete query.nombre;
      }
    }
    if (query.descripcion && typeof query.descripcion === 'string') {
      const value = query.descripcion.trim();
      if (value) {
        query.descripcion = { $regex: value, $options: 'i' };
      } else {
        delete query.descripcion;
      }
    }
    return query;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGarmentFamilies({
        ...filters,
        query: buildBackendQuery(filters.query),
      });
      setData(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las familias de prendas.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch laundry name if coming from CentroParametrizacionPage
  useEffect(() => {
    if (idLavanderia) {
      const fetchLaundryName = async () => {
        try {
          const { fetchLaundryById } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
          const laundry = await fetchLaundryById(idLavanderia);
          setLaundryName(laundry?.nombre || '');
        } catch (e) {
          console.error('Error fetching laundry:', e);
        }
      };
      fetchLaundryName();
    }
  }, [idLavanderia]);

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({ ...prev, query: newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, query: {}, page: 1 }));
  };

  const handleRemoveFilter = (filterKey) => {
    setFilters((prev) => {
      const newQuery = { ...prev.query };
      delete newQuery[filterKey];
      return { ...prev, query: newQuery, page: 1 };
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await updateGarmentFamily(editingItem._id, formData);
        toast({ title: 'Éxito', description: 'Familia de prendas actualizada correctamente.' });
      } else {
        await createGarmentFamily(formData);
        toast({ title: 'Éxito', description: 'Familia de prendas creada correctamente.' });
      }
      fetchData();
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: editingItem
          ? 'No se pudo actualizar la familia de prendas.'
          : 'No se pudo crear la familia de prendas.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'edit') {
      handleEdit(row);
    } else if (actionType === 'delete') {
      handleDelete(row);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteGarmentFamily(itemToDelete._id);
      toast({ title: 'Éxito', description: 'Familia de prendas eliminada correctamente.' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la familia de prendas.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleExport = async () => {
    try {
      await exportGarmentFamilies(buildBackendQuery(filters.query));
      toast({ title: 'Éxito', description: 'Datos exportados correctamente.' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron exportar los datos.',
        variant: 'destructive',
      });
    }
  };

  const formFields = garmentFamiliesColumns
    .filter((col) => col.id !== 'actions')
    .map((col) => ({
      ...col,
      name: col.id,
      placeholder:
        col.id === 'nombre' ? 'Ej: Ropa de cama' :
        col.id === 'descripcion' ? 'Ej: Prendas utilizadas en habitaciones' : undefined,
      defaultValue: col.id === 'estado' ? 'Activo' : undefined,
    }));

  const filterFields = [
    {
      id: 'nombre',
      name: 'nombre',
      label: 'Nombre',
      type: 'text',
      placeholder: 'Buscar por nombre...',
    },
    {
      id: 'descripcion',
      name: 'descripcion',
      label: 'Descripción',
      type: 'text',
      placeholder: 'Buscar por descripción...',
    },
    {
      name: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: '', label: 'Todos' },
        { value: 'Activo', label: 'Activo' },
        { value: 'Inactivo', label: 'Inactivo' },
      ],
    },
  ];

  return (
    <div className="container mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                {idLavanderia && (
                  <Button asChild variant="outline" size="icon" className="shrink-0">
                    <Link to={`/parametrizacion/centros/${idLavanderia}`}>
                      <ArrowLeft className="h-5 w-5" />
                    </Link>
                  </Button>
                )}
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Layers className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl">
                    Familia de Prendas
                    {laundryName && (
                      <span className="text-sm font-normal text-muted-foreground ml-2">
                        — {laundryName}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {idLavanderia
                      ? 'Gestiona las familias de prendas del centro de lavado.'
                      : 'Gestiona las familias de prendas para su clasificación'}
                  </CardDescription>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <ExportExcelButton
                  data={data}
                  columns={columnsExcel}
                  fileName="familias_prendas"
                  onExport={handleExport}
                />
                <Button
                  onClick={() => {
                    setEditingItem(null);
                    setIsModalOpen(true);
                  }}
                  className="gap-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  Nueva Familia
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsFilterOpen(true)}
                  className="gap-2"
                >
                  <FilterIcon className="h-4 w-4" />
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <AppliedFilters
              filters={filters.query}
              fields={filterFields}
              onClearFilters={handleClearFilters}
              onRemoveFilter={handleRemoveFilter}
            />
            <DataTable
              columns={garmentFamiliesColumns}
              data={data}
              isLoading={loading}
              onAction={handleAction}
              page={filters.page}
              limit={filters.limit}
              totalRecords={totalRecords}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </CardContent>
        </Card>
      </motion.div>

      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        item={editingItem}
        fields={formFields}
        title={editingItem ? 'Editar Familia de Prendas' : 'Nueva Familia de Prendas'}
      />

      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilters={handleFilter}
        onApply={handleFilter}
        fields={filterFields}
        currentFilters={filters.query}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la familia "{itemToDelete?.nombre}". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GarmentFamiliesPage;
